import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';

interface ProtectedSuperAdminRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Protects the Super Admin route tree using the local authentication
 * abstraction.
 *
 * Flow:
 * 1. Read the current session through useAuth().
 * 2. Redirect unauthenticated visitors to /login.
 * 3. Require session.user.isSuperAdmin === true (sourced from the
 *    backend's GET /api/representative/auth/session response).
 * 4. Render an access-denied screen for authenticated non-super-admin users.
 *
 * The isSuperAdmin check is intentionally based on the current
 * AuthUser shape. Context-type validation belongs to the future
 * Identity/Permissions wiring once a real UserContext can be resolved
 * from activeContextId.
 */
export function ProtectedSuperAdminRoute({
  children,
  redirectTo = '/login',
}: ProtectedSuperAdminRouteProps) {
  const { session, isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{ from: location }}
      />
    );
  }

  if (!session?.user.isSuperAdmin) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

function AccessDenied() {
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    window.location.replace('/login');
  };

  return (
    <div
      dir="rtl"
      className="flex min-h-screen items-center justify-center bg-[var(--vania-background,#0b0d12)] px-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-[var(--vania-border,rgba(255,255,255,0.1))] bg-[var(--vania-surface-1,rgba(20,22,30,0.72))] p-8 text-center shadow-2xl backdrop-blur-xl">
        <h1 className="mb-2 text-lg font-semibold text-[var(--vania-text-primary,#f5f6f8)]">
          دسترسی مجاز نیست
        </h1>

        <p className="mb-6 text-sm text-[var(--vania-text-secondary,#a6acb8)]">
          این حساب به پنل مدیر کل دسترسی ندارد.
        </p>

        <button
          type="button"
          onClick={handleLogout}
          className="rounded-lg bg-[var(--vania-accent,#5b8cff)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
        >
          خروج
        </button>
      </div>
    </div>
  );
}
